"use client";

import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink } from "@trpc/client";
import { PropsWithChildren, useContext, useState } from "react";
import { api } from "./client";
import superjson from "superjson";
import { env } from "@ept/env/src/env-frontend";
import { toast } from "react-toastify";
import { errorFormatter } from "../utils";
import { TotpContext } from "../TotpProvider";
import { v4 } from "uuid";
import { makeQueryClient } from "./getQueryClient";
const noErrorPaths = [/auth\.*/, /meRouter\.*/];
let clientQueryClientSingleton: QueryClient;
function getQueryClient() {
  if (typeof window === "undefined") {
    return makeQueryClient();
  }
  return clientQueryClientSingleton ??= makeQueryClient({
    queryCache: new QueryCache({
      onError: errorHandler
    }),
    mutationCache: new MutationCache({
      onError: errorHandler
    })
  });
}
const errorHandler = (err: unknown) => {
  const errCast = err as {
    data?: {
      path: string;
    };
    message?: string;
  };
  if (noErrorPaths.some(path => path.test(errCast.data?.path ?? ""))) return;
  if (typeof errCast === "object" && errCast !== null && "message" in errCast) {
    toast.error(errorFormatter(errCast.message as string));
  } else {
    console.error("Unexpected error:", err);
  }
};
function getUrl() {
  const base = (() => {
    if (typeof window !== "undefined") {
      return env.NEXT_PUBLIC_SERVERSIDE_HOST;
    }
    return process.env.SERVERSIDE_HOST;
  })();
  return `${base}/trpc`;
}
export default function QueryProvider({
  children
}: PropsWithChildren) {
  const {
    setIsTotpDialogOpen
  } = useContext(TotpContext);
  const queryClient = getQueryClient();
  const checkTotpSuccess = async () => {
    return new Promise(resolve => {
      const interval = setInterval(() => {
        const totpSuccess = localStorage.getItem("totpSuccess");
        if (totpSuccess === "true") {
          localStorage.removeItem("totpSuccess");
          clearInterval(interval);
          resolve(true);
        }
      }, 200);
      setTimeout(() => {
        clearInterval(interval);
        resolve(false);
      }, 60000);
    });
  };
  const [trpcClient] = useState(() => api.createClient({
    links: [httpBatchLink({
      // TODO: define the route in api package
      transformer: superjson,
      url: getUrl(),
      async fetch(url, options) {
        const fetchResponse = await fetch(url, {
          ...options,
          headers: {
            ...options?.headers,
            "x-request-id": v4()
          },
          credentials: "include"
        });
        const data = await fetchResponse.clone().json();
        const userLocked = data.find((d: any) => d.error?.json?.message === "general.locked_out");

        // if userLocked redirect to /locked-out and logout
        if (userLocked && !window.location.pathname.endsWith("locked-out")) {
          window.location.href = "/locked-out";
          return fetchResponse;
        }
        const requiresTotp = data.find((d: any) => d.error?.json?.message === "general.totp_required");
        if (requiresTotp) {
          localStorage.removeItem("totpSuccess");
          setIsTotpDialogOpen(true);
          const res = await checkTotpSuccess();
          if (res) {
            return fetch(url, {
              ...options,
              credentials: "include"
            });
          } else {
            throw new Error("TOTP invalid");
          }
        }
        return fetchResponse;
      }
    })]
  }));
  return <api.Provider client={trpcClient} queryClient={queryClient} data-sentry-element="unknown" data-sentry-component="QueryProvider" data-sentry-source-file="queryProvider.tsx">
      <QueryClientProvider client={queryClient} data-sentry-element="QueryClientProvider" data-sentry-source-file="queryProvider.tsx">
        <>{children}</>
      </QueryClientProvider>
    </api.Provider>;
}